import { BaseText, Headline3, Meta, theme, Title } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import {
  LoadingSpinner,
  TerminateButton,
} from 'components/ongoingParking/OngoingParkingComponents';
import AccountContext from 'context/AccountContext';
import useDurationFromDate from 'hooks/useDurationFromDate';
import useParkingZoneWithEVCheck from 'hooks/useParkingZoneWithEVCheck';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { country } from 'states/persistInStorage';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { useUiStore } from 'stores/uiStore';
import styled from 'styled-components/native';
import {
  CenterVerticalRow,
  ColumnWithMargins,
  RowSpaceBetween as RowStyle,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { ChargingSession, CountryCode } from 'types/generatedSchemaTypes';
import { currencyFormatter, numberFormatter } from 'utils/commonUtils';
import { getPaymentMethodDetails } from 'utils/paymentUtils';
import { getTimeFromDate } from 'utils/timeUtils';
import { StatusContainer } from './styles';

type ChargingStatusProps = {
  session: ChargingSession;
};

const StyledTitle = styled(Title)`
  font-size: ${(p) => p.theme.normalising.fontPixel(48)}px;
  line-height: ${(p) => p.theme.normalising.fontPixel(52)}px;
`;

const RowSpaceBetween = styled(RowStyle)`
  border-bottom-color: #14353d;
`;

const OngoingChargingStatus: React.FC<ChargingStatusProps> = ({ session }) => {
  const { t } = useTranslation();
  const {
    customer: { paymentCards, companyBenefits },
  } = useContext(AccountContext);
  const { energyInWatts, status, stationId, evseId } = session;
  const selectedChargingTariff = useParkzoneStore(
    (state) => state.selectedChargingPricingInfoSegment
  );

  const startTime = session?.startTime
    ? new Date(session?.startTime)
    : new Date();
  const chargingPrice = session.priceInCents || 0;
  const duration = useDurationFromDate(startTime, 5000, [
    'hours',
    'minutes',
    'seconds',
  ]);
  const chargingCountry = session.countryCode as CountryCode;
  const startingTime = getTimeFromDate(startTime, chargingCountry || country());
  const totalPrice = chargingPrice / 100;
  const currentCost = currencyFormatter(
    totalPrice,
    chargingCountry || country()
  );

  const chargingOngoing = status === 'IN_PROGRESS';
  const chargingStarting = status === 'START_IN_QUEUE';
  const chargingEnding = status === 'STOP_IN_QUEUE';

  const energyConsumedKiloWatts = (energyInWatts || 0) / 1000;
  const energyUsed = `${
    chargingCountry
      ? numberFormatter(energyConsumedKiloWatts, chargingCountry)
      : energyConsumedKiloWatts
  } kWh`;

  const getTitleText = () => {
    switch (status) {
      case 'IN_PROGRESS':
        return t('evCharging.charging');
      case 'START_IN_QUEUE':
        return t('evCharging.starting');
      case 'STOP_IN_QUEUE':
        return t('evCharging.stopping');
      default:
        return '';
    }
  };
  const titleText = getTitleText();

  const parkingZoneResult = useParkingZoneWithEVCheck(
    session.parkingZoneUid ?? '',
    'cache-first',
    true
  );
  const chargingStations =
    parkingZoneResult?.data?.parkZone?.parkingZoneChargingStations;
  const stationData = chargingStations?.find(
    (chargingStation) => chargingStation?.id === stationId
  );
  const evsePublicDisplayId = stationData?.evses?.find(
    (evse) => evse.id === evseId
  )?.publicDisplayId;

  const { paymentMethodInfo, paymentMethodLogo } = getPaymentMethodDetails(
    session,
    paymentCards,
    companyBenefits
  );

  return (
    <StatusContainer>
      <StyledTitle testID="charging-status-title" color={theme.colors.white}>
        {titleText}
      </StyledTitle>
      {evsePublicDisplayId && !chargingEnding && (
        <ColumnWithMargins mt={5} mb={20}>
          <BaseText center color={theme.colors.white}>
            {`${t('evCharging.charger')}  ${evsePublicDisplayId}`} •{' '}
            {selectedChargingTariff?.title}
          </BaseText>
        </ColumnWithMargins>
      )}
      {chargingEnding && (
        <ColumnWithMargins mt={0} mb={20}>
          <BaseText
            center
            color={theme.colors.white}
            testID={'ongoing-charging-stopping-info'}>
            {t('evCharging.stoppingInfo')}
          </BaseText>
        </ColumnWithMargins>
      )}
      <StyledWrapper mb={theme.normalising.heightPixel(40)} maxWidth={550}>
        <ColumnWithMargins ml={20} mr={20}>
          <RowSpaceBetween withBottomBorder>
            <Meta color={theme.colors.white}>
              {chargingEnding
                ? t('evCharging.durationStopped')
                : t('parking.duration')}
            </Meta>
            <Meta testID="ongoing-charging-duration" color={theme.colors.white}>
              {session.startTime ? duration : 0}
            </Meta>
          </RowSpaceBetween>
          {session.startTime && (
            <RowSpaceBetween withBottomBorder>
              <Meta color={theme.colors.white}>{t('evCharging.started')}</Meta>
              <Meta
                testID="ongoing-charging-start-time"
                color={theme.colors.white}>
                {startingTime}
              </Meta>
            </RowSpaceBetween>
          )}
          <RowSpaceBetween withBottomBorder>
            <Meta color={theme.colors.white}>
              {chargingEnding
                ? t('evCharging.energyUsedStopped')
                : t('evCharging.energyUsed')}
            </Meta>
            <Meta
              testID="ongoing-charging-energy-used"
              color={theme.colors.white}>
              {energyUsed}
            </Meta>
          </RowSpaceBetween>
          {paymentMethodInfo && (
            <RowSpaceBetween withBottomBorder>
              <Meta color={theme.colors.white}>
                {t('services.paymentMethod')}
              </Meta>
              <CenterVerticalRow>
                {paymentMethodLogo && (
                  <Icon
                    name={paymentMethodLogo.logoName}
                    size={paymentMethodLogo.logoSize}
                    color={theme.colors.white}
                  />
                )}
                <ColumnWithMargins ml={paymentMethodLogo ? 8 : 0}>
                  <Meta
                    testID="ongoing-charging-payment-method"
                    color={theme.colors.white}>
                    {paymentMethodInfo}
                  </Meta>
                </ColumnWithMargins>
              </CenterVerticalRow>
            </RowSpaceBetween>
          )}
          <RowSpaceBetween>
            <Meta color={theme.colors.white}>
              {chargingEnding
                ? t('evCharging.estimatedCost')
                : t('evCharging.currentCost')}
            </Meta>
            <Headline3
              bold
              testID="ongoing-charging-cost"
              color={theme.colors.white}>
              {currentCost}
            </Headline3>
          </RowSpaceBetween>
        </ColumnWithMargins>
      </StyledWrapper>
      {chargingOngoing && (
        <>
          <TerminateButton
            testID={'ongoing-charging-stop-button'}
            onPress={() => useUiStore.getState().setSheetModal('stopCharging')}
            bigger
          />
          <Meta color={theme.colors.white}>{t('evCharging.stop')}</Meta>
        </>
      )}
      {chargingStarting && (
        <>
          <TerminateButton
            testID={'ongoing-charging-cancel-button'}
            onPress={() =>
              useUiStore.getState().setSheetModal('cancelCharging')
            }
            action="CANCEL"
            bigger
          />
          <Meta color={theme.colors.white}>{t('button.cancel')}</Meta>
        </>
      )}
      {chargingEnding && <LoadingSpinner />}
    </StatusContainer>
  );
};

export default OngoingChargingStatus;
